/** Say something out loud, the best way this deployment can.
 *
 * Two engines sit behind one call:
 *   1. our server's Piper voice — the same on every device, and the reason
 *      an iPhone no longer gets whatever Apple happens to have installed;
 *   2. the browser's own speechSynthesis, used when the server has no voice
 *      models, is unreachable, or the audio will not play.
 *
 * The fallback is not a nicety. `onEnd` is what re-arms the microphone in a
 * conversation, so every path through here — success, failure, or a browser
 * that blocks autoplay — must end by calling it exactly once. A path that
 * silently does not is a conversation that appears to freeze.
 */
import * as api from "../api/speech";
import type { VoiceId } from "../api/speech";
import { cancelSpeech, speak } from "./tts";

export interface SpeakOptions {
  /** Speech locale for the browser fallback, e.g. "de-DE". */
  lang?: string;
  /** Server voice to use; omitted means the learner's saved preference. */
  voice?: VoiceId | null;
  /** Fires once when the audio finishes, however it was produced. */
  onEnd?: () => void;
}

/** ONE audio element for the whole session, deliberately.
 *
 * iOS only lets a page play audio from an element it has already played
 * during a real user gesture. Building a `new Audio()` per reply means
 * every reply after the first is a fresh, un-blessed element — and because
 * we fetch the recording first, the gesture has expired by the time play()
 * is called anyway. The result is exactly what the client saw: the partner
 * speaks its opening line and is silent from then on.
 *
 * So: one element, unlocked once on the tap that starts the conversation,
 * reused for every reply after that. */
let element: HTMLAudioElement | null = null;
let currentUrl: string | null = null;

/** A 0.05s silent WAV. Playing it inside a click is what blesses the
 *  element on iOS; it makes no sound. */
const SILENCE =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

function audio(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (element === null) element = new Audio();
  return element;
}

/** Call this from a real user gesture — the button that starts a
 *  conversation — so the partner can speak for the rest of the session.
 *  Safe to call more than once, and harmless where it is not needed. */
export function unlockVoice(): void {
  const el = audio();
  if (!el) return;
  try {
    el.src = SILENCE;
    // Kick it off inside the gesture; whether it finishes does not matter.
    void el.play()?.then(
      () => el.pause(),
      () => undefined,   // blocked anyway: nothing more we can do here
    );
  } catch {
    // No audio support at all. speakBest falls back to the browser voice.
  }
}
/** Only the most recent utterance may call back — a superseded turn must
 *  not re-arm the microphone underneath the one that replaced it. */
let token = 0;

function releaseAudio(): void {
  // The ELEMENT is kept — it is the unlocked one. Only the source goes.
  if (element) {
    element.pause();
    element.onended = null;
    element.onerror = null;
  }
  if (currentUrl) {
    URL.revokeObjectURL(currentUrl); // an object URL leaks until revoked
    currentUrl = null;
  }
}

/** Stop whatever is speaking, from either engine. */
export function cancelVoice(): void {
  token += 1;
  releaseAudio();
  cancelSpeech();
}

/** Speak `text`, preferring the server voice. Resolves when playback has
 *  STARTED (or the fallback has), not when it finishes — finishing is what
 *  `onEnd` is for. */
export async function speakBest(text: string, opts: SpeakOptions = {}): Promise<void> {
  const { lang, voice, onEnd } = opts;
  cancelVoice();
  const mine = token;

  const done = () => {
    if (mine !== token) return; // superseded
    releaseAudio();
    onEnd?.();
  };

  const browser = () => {
    if (mine !== token) return;
    // `speak` returns false where the browser has no synthesis at all; the
    // caller still has to be released, or the mic never re-arms.
    if (!speak(text, { lang, onEnd: done })) done();
  };

  let blob: Blob;
  try {
    blob = await api.say(text, voice);
  } catch {
    browser();
    return;
  }
  if (mine !== token) return;

  const el = audio();
  if (!el) {
    browser();
    return;
  }
  try {
    const url = URL.createObjectURL(blob);
    currentUrl = url;
    el.onended = done;
    el.onerror = browser;        // a codec the device won't play
    el.src = url;
    await el.play();
  } catch {
    // Autoplay blocked, or no Audio at all. The browser voice is subject to
    // the same gesture rules, but it is the only other thing we have.
    releaseAudio();
    browser();
  }
}
